import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { userService } from '../services/user-service';
import { UserItem, CreateUserData, UpdateUserData } from '../types/user.types';

interface UserState {
  users: UserItem[];
  isLoading: boolean;
  error: string | null;

  fetchUsers: () => Promise<void>;
  createUser: (data: CreateUserData) => Promise<UserItem>;
  updateUser: (id: string, data: UpdateUserData) => Promise<UserItem>;
  deleteUser: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useUserStore = create<UserState>()(
  devtools(
    (set) => ({
      users: [],
      isLoading: false,
      error: null,

      fetchUsers: async () => {
        try {
          set({ isLoading: true, error: null });
          const users = await userService.getUsers();
          set({ users, isLoading: false });
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al cargar usuarios',
            isLoading: false,
          });
          throw error;
        }
      },

      createUser: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const user = await userService.createUser(data);
          set((state) => ({
            users: [...state.users, user],
            isLoading: false,
          }));
          return user;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al crear usuario',
            isLoading: false,
          });
          throw error;
        }
      },

      updateUser: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const user = await userService.updateUser(id, data);
          set((state) => ({
            users: state.users.map((u) => (u.id === id ? user : u)),
            isLoading: false,
          }));
          return user;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al actualizar usuario',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteUser: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await userService.deleteUser(id);
          set((state) => ({
            users: state.users.filter((u) => u.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al eliminar usuario',
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'UserStore' }
  )
);

export default useUserStore;
